import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { SettingsPanel } from './SettingsPanel';

/**
 * The connection settings, on their own. They used to share the debug drawer with the log, where a
 * dozen fields sat between a tester and the turns they opened the drawer to read — and where a
 * setting's effect and the frames it produced fought for the same height.
 *
 * A dialog rather than a second drawer: settings are changed in a moment and then left alone, and
 * Radix gives the modal its focus trap, its Escape and its labelling.
 */
export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-shell/80 fixed inset-0 z-40 backdrop-blur-sm"
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.98 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                className="dbg-skin rounded-panel scroll-slim fixed top-1/2 left-1/2 z-50 flex max-h-[88vh] w-[min(620px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden border shadow-2xl"
              >
                <header className="dbg-head">
                  <Dialog.Title className="dbg-title">Connection</Dialog.Title>
                  <Dialog.Close asChild>
                    <button type="button" aria-label="Close the settings" className="dbg-icon">
                      <X className="size-3.5" />
                    </button>
                  </Dialog.Close>
                </header>

                <Dialog.Description className="dbg-note px-[18px] pt-3">
                  Where the gateway is, which model answers, and who the client says it is. The
                  handshake fields apply on the next sign-in.
                </Dialog.Description>

                <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-[18px] pb-[18px]">
                  <SettingsPanel />
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
